// Sound, made in the browser: no audio files. Web Audio synthesises everything, and three.js
// PositionalAudio puts each source where it is on the planet, so it gets louder as you walk up:
//   the bar      a quiet exotica loop (vibraphone chords, upright bass, bongos, the odd bird
//                call, after Martin Denny), and the robot's shaker, ice and pours
//   the fire     a low rumble and crackles
//   the lagoon   water lapping
//   the hammock  its ropes creaking when you get in or out
//   everywhere   crickets
//   anywhere     the loose things (physics.js): thuds and splashes where they happen
//   the console  the launch console: a CRT's hum while you lean in to it, relay clicks, the
//                guard's clack, countdown beeps; and the rocket's roar in an engine test
//   the door     daytime in Honolulu: a breeze in the trees, zebra doves, the odd whistle; faint
//                through the open door as you walk up, all round you once you're through, while
//                the planet's night goes quiet (away())
// Off until the visitor turns it on (browsers only start audio from a click or key), then
// remembered (localStorage `world-sound`); paused while the tab is hidden.
import * as THREE from 'three';

const KEY = 'world-sound';

export function createSound({ scene, camera, spots }) {
  let ctx = null, listener = null, on = false, timer = 0;
  const inputs = {};   // name -> GainNode that feeds a positional source
  const sources = {};  // name -> its PositionalAudio
  let nextBeat = 0, beat = 0, nextBird = 0, nextCrackle = 0, nextCricket = 0, nextDove = 0, nextWhistle = 0;
  let isAway = false, dayAll = null; // dayAll: the door's daytime, all round you (see away())
  let noiseWhite, noiseBrown;

  const remembered = () => { try { return localStorage.getItem(KEY) === '1'; } catch (e) { return false; } };
  const remember = (v) => { try { localStorage.setItem(KEY, v ? '1' : '0'); } catch (e) {} };

  function buffers() {
    const len = ctx.sampleRate * 2;
    noiseWhite = ctx.createBuffer(1, len, ctx.sampleRate);
    noiseBrown = ctx.createBuffer(1, len, ctx.sampleRate);
    const w = noiseWhite.getChannelData(0), b = noiseBrown.getChannelData(0);
    let last = 0;
    for (let i = 0; i < len; i++) {
      w[i] = Math.random() * 2 - 1;
      last = (last + 0.02 * w[i]) / 1.02; // brown: integrated white
      b[i] = last * 3.5;
    }
  }
  const noiseSource = (buf, loop = true) => { const s = ctx.createBufferSource(); s.buffer = buf; s.loop = loop; return s; };

  // A source at a place: its input (a GainNode) feeds a PositionalAudio there.
  function place(name, pos, { ref = 3, max = 60, rolloff = 1.3, gain = 1 } = {}) {
    if ((spots.off || []).includes(name)) gain = 0; // not on this planet
    const a = new THREE.PositionalAudio(listener);
    a.setRefDistance(ref); a.setMaxDistance(max); a.setRolloffFactor(rolloff); a.setDistanceModel('inverse');
    a.position.copy(pos);
    scene.add(a);
    const g = ctx.createGain();
    g.gain.value = gain;
    a.setNodeSource(g);
    inputs[name] = g;
    sources[name] = a;
    return g;
  }

  function start() {
    listener = new THREE.AudioListener();
    camera.add(listener);
    ctx = listener.context;
    listener.setMasterVolume(0.8);
    buffers();
    // everywhere: crickets (not positional)
    const everywhere = new THREE.Audio(listener);
    const eg = ctx.createGain();
    eg.gain.value = 0.5;
    everywhere.setNodeSource(eg);
    inputs.everywhere = eg;
    place('bar', spots.bar, { ref: 3.5, max: 80, rolloff: 1.1 });
    place('fire', spots.fire, { ref: 1.5, max: 30, rolloff: 1.6 });
    place('lagoon', spots.lagoon, { ref: 2, max: 30, rolloff: 1.5 });
    place('hammock', spots.hammock, { ref: 1.5, max: 20, rolloff: 1.5 });
    place('loose', spots.bar, { ref: 1.5, max: 25, rolloff: 1.5 }); // moved to wherever it happens
    if (spots.console) place('console', spots.console, { ref: 1, max: 25, rolloff: 1.4 });
    if (spots.rocket) place('rocket', spots.rocket, { ref: 3, max: 70, rolloff: 1.1 });
    // the daytime through the door: one source (inputs.day), out at the door and all round you
    if (spots.door) {
      const d = ctx.createGain(), at = place('door', spots.door, { ref: 1.2, max: 25, rolloff: 1.6, gain: isAway ? 0 : 1 });
      const all = new THREE.Audio(listener), ag = ctx.createGain();
      ag.gain.value = isAway ? 1 : 0;
      all.setNodeSource(ag);
      d.connect(at); d.connect(ag);
      inputs.day = d;
      dayAll = ag;
      // a breeze in the leaves, coming and going in gusts, over a low push of air
      const leaves = noiseSource(noiseWhite), lf = ctx.createBiquadFilter(), lg = ctx.createGain();
      lf.type = 'bandpass'; lf.frequency.value = 2400; lf.Q.value = 0.5; lg.gain.value = 0.06;
      const air = noiseSource(noiseBrown), af = ctx.createBiquadFilter(), ag2 = ctx.createGain();
      af.type = 'lowpass'; af.frequency.value = 380; ag2.gain.value = 0.12;
      for (const [hz, depth, g] of [[0.07, 0.035, lg], [0.13, 0.02, lg], [0.05, 0.06, ag2]]) {
        const lfo = ctx.createOscillator(), dg = ctx.createGain();
        lfo.frequency.value = hz; dg.gain.value = depth;
        lfo.connect(dg).connect(g.gain); lfo.start();
      }
      leaves.connect(lf).connect(lg).connect(d); leaves.start();
      air.connect(af).connect(ag2).connect(d); air.start();
    }

    // the fire's low rumble
    { const s = noiseSource(noiseBrown), f = ctx.createBiquadFilter(), g = ctx.createGain();
      f.type = 'lowpass'; f.frequency.value = 320; g.gain.value = 0.5;
      s.connect(f).connect(g).connect(inputs.fire); s.start(); }
    // the lagoon: low noise swelling and falling like small waves on the shore
    { const s = noiseSource(noiseBrown), f = ctx.createBiquadFilter(), g = ctx.createGain();
      f.type = 'lowpass'; f.frequency.value = 650; g.gain.value = 0.22;
      for (const [hz, depth] of [[0.17, 0.12], [0.11, 0.08]]) {
        const lfo = ctx.createOscillator(), d = ctx.createGain();
        lfo.frequency.value = hz; d.gain.value = depth;
        lfo.connect(d).connect(g.gain); lfo.start();
      }
      s.connect(f).connect(g).connect(inputs.lagoon); s.start(); }
    const t = ctx.currentTime + 0.1;
    nextBeat = t; nextBird = t + 6; nextCrackle = t; nextCricket = t; nextDove = t + 2; nextWhistle = t + 9;
    if (isAway) night(0, 0);
    timer = setInterval(schedule, 100);
    document.addEventListener('visibilitychange', onVisible);
  }
  const onVisible = () => { if (!ctx) return; if (document.hidden) ctx.suspend(); else if (on) ctx.resume(); };

  /* ---------- Voices ---------- */
  const env = (g, t, peak, attack, decay) => {
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
  };
  const midi = (n) => 440 * Math.pow(2, (n - 69) / 12);
  function vibe(n, t, v = 0.12) { // a vibraphone: bright bar, fast motor tremolo
    const out = ctx.createGain(), trem = ctx.createGain(), lfo = ctx.createOscillator(), depth = ctx.createGain();
    lfo.frequency.value = 5.2; depth.gain.value = 0.35; trem.gain.value = 0.75;
    lfo.connect(depth).connect(trem.gain);
    for (const [mult, amp] of [[1, 1], [4, 0.18], [10, 0.04]]) {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.frequency.value = midi(n) * mult;
      env(g, t, v * amp, 0.005, mult === 1 ? 2.4 : 0.4);
      o.connect(g).connect(trem);
      o.start(t); o.stop(t + 2.6);
    }
    trem.connect(out).connect(inputs.bar);
    lfo.start(t); lfo.stop(t + 2.6);
  }
  function bass(n, t) { // soft upright bass
    const o = ctx.createOscillator(), g = ctx.createGain(), f = ctx.createBiquadFilter();
    o.type = 'triangle'; o.frequency.value = midi(n);
    f.type = 'lowpass'; f.frequency.value = 500;
    env(g, t, 0.22, 0.01, 0.7);
    o.connect(f).connect(g).connect(inputs.bar);
    o.start(t); o.stop(t + 0.8);
  }
  function bongo(hi, t, v = 0.16) {
    const o = ctx.createOscillator(), g = ctx.createGain();
    const f0 = hi ? 560 : 400;
    o.frequency.setValueAtTime(f0, t);
    o.frequency.exponentialRampToValueAtTime(f0 * 0.62, t + 0.09);
    env(g, t, v, 0.002, 0.12);
    o.connect(g).connect(inputs.bar);
    o.start(t); o.stop(t + 0.16);
  }
  function bird(t) { // a tropical bird call, whistled
    const o = ctx.createOscillator(), g = ctx.createGain(), fm = ctx.createOscillator(), fmg = ctx.createGain();
    const n = 3 + Math.floor(Math.random() * 3), base = 2200 + Math.random() * 900;
    fm.frequency.value = 28; fmg.gain.value = 180;
    fm.connect(fmg).connect(o.frequency);
    for (let i = 0; i < n; i++) {
      const s = t + i * 0.16;
      o.frequency.setValueAtTime(base, s);
      o.frequency.exponentialRampToValueAtTime(base * (i % 2 ? 1.35 : 1.6), s + 0.1);
      g.gain.setValueAtTime(0.0001, s);
      g.gain.exponentialRampToValueAtTime(0.05, s + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, s + 0.13);
    }
    o.connect(g).connect(inputs.bar);
    o.start(t); fm.start(t); o.stop(t + n * 0.16 + 0.1); fm.stop(t + n * 0.16 + 0.1);
  }
  function dove(t) { // a zebra dove: a soft, quick run of coos, then a longer one
    const n = 6 + Math.floor(Math.random() * 5), f0 = 560 + Math.random() * 90;
    for (let i = 0; i <= n; i++) {
      const s = t + i * 0.085, len = i === n ? 0.32 : 0.06;
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.frequency.setValueAtTime(f0 * (i === n ? 1.12 : 1), s);
      o.frequency.exponentialRampToValueAtTime(f0 * 0.86, s + len);
      env(g, s, 0.05, 0.012, len);
      o.connect(g).connect(inputs.day); o.start(s); o.stop(s + len + 0.05);
    }
  }
  function whistle(t) { // a cardinal somewhere off in the trees: two or three clear slurs
    for (let i = 0, n = 2 + Math.floor(Math.random() * 2); i < n; i++) {
      const s = t + i * 0.42, o = ctx.createOscillator(), g = ctx.createGain(), hi = 2600 + Math.random() * 500;
      o.frequency.setValueAtTime(hi, s); o.frequency.exponentialRampToValueAtTime(hi * 0.55, s + 0.3);
      env(g, s, 0.025, 0.02, 0.3);
      o.connect(g).connect(inputs.day); o.start(s); o.stop(s + 0.36);
    }
  }
  function burst(input, t, { dur = 0.03, type = 'highpass', hz = 1800, q = 0.7, v = 0.2 } = {}) {
    const s = noiseSource(noiseWhite, false), f = ctx.createBiquadFilter(), g = ctx.createGain();
    f.type = type; f.frequency.value = hz; f.Q.value = q;
    env(g, t, v, 0.002, dur);
    s.connect(f).connect(g).connect(input);
    s.start(t, Math.random() * 1.5); s.stop(t + dur + 0.05);
  }
  function chirp(t) { // a cricket: three quick pulses of a high tone
    const o = ctx.createOscillator(), g = ctx.createGain();
    o.frequency.value = 4400 + Math.random() * 500;
    g.gain.value = 0;
    for (let i = 0; i < 3; i++) { g.gain.setValueAtTime(0.012, t + i * 0.035); g.gain.setValueAtTime(0, t + i * 0.035 + 0.02); }
    o.connect(g).connect(inputs.everywhere);
    o.start(t); o.stop(t + 0.15);
  }

  // Dm9 · G13 · Cmaj9 · A7(b9), two bars each, as rolled vibraphone chords over a walking bass
  const CHORDS = [[50, [65, 69, 72, 76]], [55, [65, 69, 71, 76]], [48, [64, 67, 71, 74]], [45, [61, 64, 67, 70]]];
  const EIGHTH = 60 / 76 / 2;
  function schedule() {
    if (!ctx || !on) return;
    const ahead = ctx.currentTime + 0.35;
    while (nextBeat < ahead) {
      const bar = Math.floor(beat / 8), [root, notes] = CHORDS[Math.floor(bar / 2) % CHORDS.length], i = beat % 8;
      if (i === 0) notes.forEach((n, k) => vibe(n, nextBeat + k * 0.035, 0.08));             // rolled chord
      if (i === 3 || i === 6) vibe(notes[(beat + bar) % notes.length] + 12, nextBeat, 0.06); // a few notes on top
      if (i === 0 || i === 4) bass(i === 0 ? root : root + 7, nextBeat);
      if ([0, 3, 5, 6].includes(i)) bongo(i % 3 !== 0, nextBeat, i === 0 ? 0.14 : 0.09);
      nextBeat += EIGHTH; beat++;
    }
    while (nextBird < ahead) { bird(nextBird); nextBird += 12 + Math.random() * 14; }
    while (nextCrackle < ahead) { burst(inputs.fire, nextCrackle, { dur: 0.01 + Math.random() * 0.04, v: 0.1 + Math.random() * 0.4 }); nextCrackle += 0.04 + Math.random() * 0.35; }
    while (nextCricket < ahead) { chirp(nextCricket); nextCricket += Math.random() < 0.7 ? 0.5 : 1.4; }
    if (inputs.day) {
      while (nextDove < ahead) { dove(nextDove); nextDove += 7 + Math.random() * 9; }
      while (nextWhistle < ahead) { whistle(nextWhistle); nextWhistle += 11 + Math.random() * 13; }
    }
  }

  /* ---------- Things that happen ---------- */
  let pourNode = null, humNode = null;
  // the console's screen humming while you're at it (a mains hum and the faint whine of a CRT)
  function humming(want) {
    if (want && !humNode && inputs.console) {
      const g = ctx.createGain(), t = ctx.currentTime;
      g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.05, t + 0.4);
      const parts = [[60, 'sine', 1], [120, 'sine', 0.5], [180, 'triangle', 0.15], [7800, 'sine', 0.02]].map(([hz, type, v]) => {
        const o = ctx.createOscillator(), og = ctx.createGain();
        o.type = type; o.frequency.value = hz; og.gain.value = v;
        o.connect(og).connect(g); o.start(); return o;
      });
      g.connect(inputs.console);
      humNode = { g, parts };
    } else if (!want && humNode) {
      const { g, parts } = humNode, t = ctx.currentTime;
      g.gain.setTargetAtTime(0.0001, t, 0.08);
      parts.forEach((o) => o.stop(t + 0.5));
      humNode = null;
    }
  }
  const fx = {
    shake(seconds = 1) { // ice rattling in a tin
      const t = ctx.currentTime;
      for (let k = 0; k * 0.11 < seconds; k++) burst(inputs.bar, t + k * 0.11, { type: 'bandpass', hz: 2600 + (k % 2) * 900, q: 1.2, dur: 0.07, v: 0.35 });
    },
    clink() { // ice into a glass
      const t = ctx.currentTime;
      [2900, 3900, 5200].forEach((hz, k) => {
        const o = ctx.createOscillator(), g = ctx.createGain();
        o.frequency.value = hz; env(g, t + k * 0.06, 0.05, 0.002, 0.3);
        o.connect(g).connect(inputs.bar); o.start(t + k * 0.06); o.stop(t + k * 0.06 + 0.35);
      });
    },
    pour(flowing) { // a thin stream into a glass
      if (flowing && !pourNode) {
        const s = noiseSource(noiseWhite), f = ctx.createBiquadFilter(), g = ctx.createGain();
        f.type = 'bandpass'; f.frequency.value = 1100; f.Q.value = 1.5;
        g.gain.setValueAtTime(0.0001, ctx.currentTime); g.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.08);
        s.connect(f).connect(g).connect(inputs.bar); s.start();
        pourNode = { s, g };
      } else if (!flowing && pourNode) {
        const { s, g } = pourNode;
        g.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.04); s.stop(ctx.currentTime + 0.3);
        pourNode = null;
      }
    },
    whoosh() { // a stone leaving your hand
      burst(inputs.lagoon, ctx.currentTime, { type: 'bandpass', hz: 900, q: 0.8, dur: 0.18, v: 0.12 });
    },
    skip(n = 1) { // a stone touching the water: a light, bright tick, softer each time
      const t = ctx.currentTime, o = ctx.createOscillator(), g = ctx.createGain();
      o.frequency.setValueAtTime(1400, t); o.frequency.exponentialRampToValueAtTime(650, t + 0.06);
      env(g, t, 0.16 / Math.sqrt(n), 0.002, 0.08);
      o.connect(g).connect(inputs.lagoon); o.start(t); o.stop(t + 0.12);
      burst(inputs.lagoon, t, { hz: 3000, dur: 0.05, v: 0.12 / Math.sqrt(n) });
    },
    plop() { // sinking
      const t = ctx.currentTime, o = ctx.createOscillator(), g = ctx.createGain();
      o.frequency.setValueAtTime(420, t); o.frequency.exponentialRampToValueAtTime(110, t + 0.18);
      env(g, t, 0.3, 0.004, 0.2);
      o.connect(g).connect(inputs.lagoon); o.start(t); o.stop(t + 0.25);
      burst(inputs.lagoon, t, { type: 'lowpass', hz: 900, dur: 0.25, v: 0.2 });
    },
    clack() { // on the sand and pebbles of the far shore
      const t = ctx.currentTime;
      burst(inputs.lagoon, t, { type: 'bandpass', hz: 2200, q: 3, dur: 0.03, v: 0.3 });
      burst(inputs.lagoon, t + 0.07, { type: 'bandpass', hz: 2600, q: 3, dur: 0.025, v: 0.15 });
    },
    toss() { // something leaving your hand, right beside you
      burst(inputs.everywhere, ctx.currentTime, { type: 'bandpass', hz: 700, q: 0.7, dur: 0.2, v: 0.2 });
    },
    thud(at, k = 0.5) { // a coconut landing: a dull knock, harder the faster it came down
      sources.loose.position.copy(at); sources.loose.updateMatrixWorld();
      const t = ctx.currentTime, o = ctx.createOscillator(), g = ctx.createGain();
      o.frequency.setValueAtTime(150 + 60 * k, t); o.frequency.exponentialRampToValueAtTime(70, t + 0.1);
      env(g, t, 0.25 + 0.45 * k, 0.003, 0.14);
      o.connect(g).connect(inputs.loose); o.start(t); o.stop(t + 0.2);
      burst(inputs.loose, t, { type: 'lowpass', hz: 700, dur: 0.05, v: 0.2 * k });
    },
    zap(at, k = 0.6) { // a neon tube catching: a crackle and a short mains buzz
      sources.loose.position.copy(at); sources.loose.updateMatrixWorld();
      const t = ctx.currentTime, o = ctx.createOscillator(), g = ctx.createGain();
      o.type = 'sawtooth'; o.frequency.value = 120;
      env(g, t, 0.12 * k, 0.004, 0.12);
      o.connect(g).connect(inputs.loose); o.start(t); o.stop(t + 0.16);
      for (let i = 0; i < 4; i++) burst(inputs.loose, t + Math.random() * 0.08, { type: 'highpass', hz: 2500 + Math.random() * 3000, dur: 0.01, v: 0.1 * k });
    },
    firework(at) { // a shell bursting overhead: a crack, then crackle
      sources.loose.position.copy(at); sources.loose.updateMatrixWorld();
      const t = ctx.currentTime, o = ctx.createOscillator(), g = ctx.createGain();
      o.frequency.setValueAtTime(90, t); o.frequency.exponentialRampToValueAtTime(40, t + 0.3);
      env(g, t, 0.5, 0.002, 0.35);
      o.connect(g).connect(inputs.loose); o.start(t); o.stop(t + 0.4);
      burst(inputs.loose, t, { type: 'lowpass', hz: 1800, dur: 0.12, v: 0.45 });
      for (let k = 0; k < 14; k++) burst(inputs.loose, t + 0.15 + Math.random() * 0.9, { type: 'highpass', hz: 3000 + Math.random() * 3000, dur: 0.012, v: 0.06 + Math.random() * 0.06 });
    },
    splash(at, k = 0.5) { // into the lagoon
      sources.loose.position.copy(at); sources.loose.updateMatrixWorld();
      const t = ctx.currentTime, o = ctx.createOscillator(), g = ctx.createGain();
      o.frequency.setValueAtTime(380, t); o.frequency.exponentialRampToValueAtTime(120, t + 0.2);
      env(g, t, 0.15 + 0.25 * k, 0.004, 0.22);
      o.connect(g).connect(inputs.loose); o.start(t); o.stop(t + 0.3);
      burst(inputs.loose, t, { type: 'lowpass', hz: 1400, dur: 0.3 + 0.2 * k, v: 0.15 + 0.25 * k });
    },
    ratchet(seconds = 0.8) { // the telescope's gears as it swings round: a quick run of brass clicks, slowing at the end
      const t = ctx.currentTime;
      for (let s = 0, n = 0; s < seconds; n++) {
        burst(inputs.everywhere, t + s, { type: 'bandpass', hz: 3400 - (n % 2) * 500, q: 5, dur: 0.012, v: 0.07 });
        s += 0.045 + 0.06 * (s / seconds) ** 2;
      }
    },
    beep(high = false) { // the countdown's last ten seconds, at the console
      if (!inputs.console) return;
      const t = ctx.currentTime, o = ctx.createOscillator(), g = ctx.createGain();
      o.type = 'square'; o.frequency.value = high ? 1760 : 1320;
      env(g, t, 0.06, 0.004, high ? 0.5 : 0.12);
      const f = ctx.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = 3200;
      o.connect(f).connect(g).connect(inputs.console); o.start(t); o.stop(t + (high ? 0.6 : 0.2));
    },
    relay() { // a relay clicking over: the console stepping to another launch
      if (!inputs.console) return;
      const t = ctx.currentTime;
      burst(inputs.console, t, { type: 'bandpass', hz: 2400, q: 4, dur: 0.012, v: 0.18 });
      burst(inputs.console, t + 0.018, { type: 'bandpass', hz: 1500, q: 3, dur: 0.02, v: 0.1 });
    },
    guard() { // the console's guard flipping up or falling shut
      if (!inputs.console) return;
      const t = ctx.currentTime;
      burst(inputs.console, t, { type: 'bandpass', hz: 900, q: 1.5, dur: 0.04, v: 0.3 });
      burst(inputs.console, t + 0.01, { type: 'bandpass', hz: 2600, q: 3, dur: 0.02, v: 0.14 });
    },
    roar(seconds = 6) { // the engine test: a deep roar and crackle, building, holding, dying away
      if (!inputs.rocket) return;
      const t = ctx.currentTime, up = 0.9, down = 1.6;
      for (const [buf, type, hz, q, v] of [[noiseBrown, 'lowpass', 220, 0.7, 1.1], [noiseWhite, 'bandpass', 520, 0.8, 0.22]]) {
        const s = noiseSource(buf), f = ctx.createBiquadFilter(), g = ctx.createGain();
        f.type = type; f.frequency.value = hz; f.Q.value = q;
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(v, t + up);
        g.gain.setValueAtTime(v, t + seconds - down);
        g.gain.exponentialRampToValueAtTime(0.0001, t + seconds);
        s.connect(f).connect(g).connect(inputs.rocket); s.start(t); s.stop(t + seconds + 0.1);
      }
      for (let k = 0; k < seconds * 30; k++) burst(inputs.rocket, t + up * 0.5 + Math.random() * (seconds - down), { type: 'highpass', hz: 1800 + Math.random() * 2500, dur: 0.008, v: 0.05 + Math.random() * 0.07 });
    },
    tick() { // a soft brass click: the telescope settling on a landing site
      burst(inputs.everywhere, ctx.currentTime, { type: 'bandpass', hz: 2600, q: 6, dur: 0.02, v: 0.12 });
    },
    chalk() { // a piece of chalk tapping the board, twice
      const t = ctx.currentTime;
      burst(inputs.everywhere, t, { type: 'bandpass', hz: 1500, q: 2, dur: 0.025, v: 0.14 });
      burst(inputs.everywhere, t + 0.07, { type: 'bandpass', hz: 1800, q: 2, dur: 0.02, v: 0.09 });
    },
    page() { // a page turning: a soft rising brush of paper
      const t = ctx.currentTime, s = noiseSource(noiseWhite, false), f = ctx.createBiquadFilter(), g = ctx.createGain();
      f.type = 'bandpass'; f.Q.value = 0.9;
      f.frequency.setValueAtTime(900, t); f.frequency.exponentialRampToValueAtTime(3200, t + 0.22);
      env(g, t, 0.09, 0.05, 0.25);
      s.connect(f).connect(g).connect(inputs.everywhere); s.start(t); s.stop(t + 0.32);
    },
    creak() { // rope taking weight round a palm
      const t = ctx.currentTime;
      for (let k = 0; k < 2; k++) {
        const o = ctx.createOscillator(), f = ctx.createBiquadFilter(), g = ctx.createGain(), s = t + k * 0.45;
        o.type = 'sawtooth';
        o.frequency.setValueAtTime(80, s); o.frequency.linearRampToValueAtTime(115, s + 0.35);
        f.type = 'bandpass'; f.frequency.value = 520; f.Q.value = 9;
        env(g, s, 0.25, 0.05, 0.35);
        o.connect(f).connect(g).connect(inputs.hammock); o.start(s); o.stop(s + 0.45);
      }
    },
  };

  const button = document.getElementById('sound-btn');
  let first = null;
  function set(v, { save = true } = {}) {
    if (first) { removeEventListener('pointerdown', first, true); removeEventListener('keydown', first, true); first = null; }
    on = v;
    if (save) remember(v);
    button.setAttribute('aria-pressed', String(v));
    button.setAttribute('aria-label', v ? 'Sound on' : 'Sound off');
    if (!v) { if (ctx) ctx.suspend(); return; }
    if (!ctx) start();
    ctx.resume();
  }
  button.hidden = false;
  button.addEventListener('click', () => set(!on));
  // remembered as on: it starts with the first click or key (browsers won't start audio before)
  if (remembered()) {
    button.setAttribute('aria-pressed', 'true');
    button.setAttribute('aria-label', 'Sound on');
    first = (e) => { if (e.target.closest && e.target.closest('#sound-btn')) return; set(true, { save: false }); };
    addEventListener('pointerdown', first, true);
    addEventListener('keydown', first, true);
  }

  // the planet's night, all of it, up or down (the door's daytime is apart from it)
  function night(to, seconds) {
    for (const [name, full] of [['bar', 1], ['fire', 1], ['lagoon', 1], ['hammock', 1], ['loose', 1], ['console', 1], ['rocket', 1], ['everywhere', 0.5]]) {
      if (!inputs[name]) continue;
      const g = inputs[name].gain;
      g.cancelScheduledValues(ctx.currentTime);
      g.setTargetAtTime(to * full, ctx.currentTime, seconds / 3 + 0.001);
    }
  }

  return {
    /** Through the door (true) or back on the planet (false): the daytime comes up all round you and the night goes, or back again, over `seconds`. */
    away(v, seconds = 0.8) {
      isAway = v;
      if (!ctx || !dayAll) return;
      const t = ctx.currentTime, k = seconds / 3 + 0.001;
      dayAll.gain.setTargetAtTime(v ? 1 : 0, t, k);
      inputs.door.gain.setTargetAtTime(v ? 0 : 1, t, k);
      night(v ? 0 : 1, seconds);
    },
    /** The console's screen humming (true) or not (false), while you're leaned in to it. */
    hum(v) { if (ctx && (on || !v)) humming(v); },
    /** Something happened: 'beep' (high), 'relay', 'guard', 'roar' (seconds), 'shake' (seconds), 'clink', 'pour' (flowing), 'creak', 'ratchet' (seconds), 'tick', 'chalk', 'page', 'thud' / 'splash' (where, how hard), 'toss', 'firework' (where), 'zap' (where, how hard). Silent while off. */
    play(name, ...args) { if (on && ctx && fx[name]) fx[name](...args); },
    get on() { return on; },
    set,
  };
}
